import React from 'react';
import ResponsiveECharts from './ResponsiveECharts';
import { baseOption, axeValeur } from './echartsTheme';

/**
 * Classement des solutions d'adaptation, en nuage de points.
 *
 *   axe X : impact de la solution — écart entre le risque de l'horizon choisi
 *           et celui de la référence (2020, ou Référence en TRACC).
 *           Axe inversé : les valeurs positives sont à gauche, les négatives
 *           à droite. Placé EN HAUT du graphique, au niveau du 0 de l'axe Y.
 *   axe Y : robustesse — écart-type du résultat entre scénarios.
 *           Axe inversé : 0 en haut ("sans regret"), les valeurs augmentent
 *           vers le bas ("Variables").
 *
 * Les deux valeurs sont calculées côté API : ce composant ne fait que placer
 * les points et colorer les quadrants.
 *
 * Couleurs des quadrants, définies par leur position À L'ÉCRAN :
 *   haut-gauche  jaune
 *   haut-droite  vert
 *   bas-gauche   rouge
 *   bas-droite   orange
 */
const COULEURS_QUADRANTS = {
  hautDroite: 'rgba(92, 191, 55, 0.35)',
  hautGauche: 'rgba(255, 210, 120, 0.35)',
  basDroite: 'rgba(178, 209, 84, 0.35)',
  basGauche: 'rgba(230, 124, 115, 0.35)',
};

// Impact considéré comme nul : entre -0.5 et +0.5 points, la ligne du tableau
// reste blanche (pas de quadrant de couleur).
const SEUIL_IMPACT_NUL = 0.5;

// Axes en entiers : les bornes sont l'arrondi supérieur du max (pas de
// décimale).
const arrondiSup = (v) => Math.max(1, Math.ceil(v - 1e-9));

// Borne haute de l'axe Y (robustesse) pour une liste de solutions.
const yMaxDe = (liste) => arrondiSup(Math.max(...liste.map((d) => d.ecartType)));

/**
 * Couleur de la ligne du tableau = couleur du quadrant où se trouve le point
 * dans le graphique (mêmes couleurs que COULEURS_QUADRANTS), ou blanc si
 * l'impact est compris entre -0.5 et +0.5.
 */
function couleurQuadrant(d, yMax) {
  if (Math.abs(d.impact) <= SEUIL_IMPACT_NUL) return '#ffffff';
  const haut = d.ecartType < yMax / 2; // Y inversé : le haut = sans regret
  const gauche = d.impact > 0; // X inversé : les valeurs positives sont à gauche
  if (haut) return gauche ? COULEURS_QUADRANTS.hautGauche : COULEURS_QUADRANTS.hautDroite;
  return gauche ? COULEURS_QUADRANTS.basGauche : COULEURS_QUADRANTS.basDroite;
}

/**
 * Couleur de chaque solution pour UN horizon : Map(nom de la solution -> couleur).
 * Chaque horizon a son propre graphique, donc ses propres échelles : le seuil
 * haut/bas se calcule sur la liste de cet horizon. Sert à repérer les
 * solutions qui ne changent pas de carré entre deux horizons (Classement.jsx).
 */
export function couleursParSolution(liste = []) {
  if (liste.length === 0) return new Map();
  const yMax = yMaxDe(liste);
  return new Map(liste.map((d) => [d.solution, couleurQuadrant(d, yMax)]));
}

const BORDURE_NOIRE = '2px solid #000000';

// Contour noir d'une ligne : bords haut et bas sur chaque cellule, bord gauche
// sur la première et bord droit sur la dernière.
const contourLigne = (premiere, derniere) => ({
  borderTop: BORDURE_NOIRE,
  borderBottom: BORDURE_NOIRE,
  ...(premiere && { borderLeft: BORDURE_NOIRE }),
  ...(derniere && { borderRight: BORDURE_NOIRE }),
});

/**
 * @param data              [{ solution, impact, ecartType }] déjà classées
 * @param solutionsStables  noms des solutions qui ne changent pas de carré de
 *                          couleur entre 2035 et 2050 : leur ligne du tableau
 *                          est entourée de noir
 */
export default function ClassementAdaptationsChart({ data = [], solutionsStables = [] }) {
  if (data.length === 0) {
    return <p className="tdb-chargement">Aucune solution à afficher.</p>;
  }

  const stables = new Set(solutionsStables);

  const impacts = data.map((d) => d.impact);
  const robustesses = data.map((d) => d.ecartType);

  // Les graduations tombent de 1 en 1 tant que l'axe reste lisible (au-delà de
  // 12, on espace d'un pas rond).
  const pasEntier = (n) => (n <= 12 ? 1 : Math.ceil(n / 6));

  // Axe X (impact) symétrique autour de 0 : de -borne à +borne, pour que la
  // lecture gain/perte soit uniforme de part et d'autre.
  const borne = arrondiSup(Math.max(...impacts.map((v) => Math.abs(v))));
  const xMin = -borne;
  const xMax = borne;
  // Axe Y (robustesse) : de 0 à l'arrondi supérieur du plus grand écart-type.
  // Le seuil "sans regret" est relatif aux solutions comparées (milieu de
  // l'axe), pas une valeur absolue.
  const yMin = 0;
  const yMax = yMaxDe(data);

  const option = {
    ...baseOption(),
    legend: { show: false },
    // L'axe X est en haut : la marge du haut accueille ses graduations, son nom
    // et les extrémités "impact fort / impact négatif". Celle de gauche
    // accueille "Robustesse" et les graduations de l'axe Y.
    grid: { left: 120, right: 40, bottom: 20, top: 70, containLabel: true },
    tooltip: {
      trigger: 'item',
      textStyle: { fontSize: 14, color: '#000000' },
      formatter: ({ data: point }) =>
        `<b>${point.numero}. ${point.nom}</b><br/>Impact : ${point.value[0].toFixed(1)} pts` +
        `<br/>Robustesse : ${point.ecartType.toFixed(2)}`,
    },
    xAxis: {
      ...axeValeur({ nom: 'Impact', formatteur: (v) => `${v}` }),
      min: xMin,
      max: xMax,
      interval: pasEntier(borne),
      // Au-dessus du graphique, au niveau du 0 de l'axe Y.
      position: 'top',
      // Positifs à gauche, négatifs à droite.
      inverse: true,
      // Le nom est centré au-dessus de l'axe, entre les deux extrémités.
      nameLocation: 'middle',
      nameGap: 40,
      nameTextStyle: { fontSize: 14, color: '#000000', fontWeight: 600, align: 'center' },
      axisLine: { show: true, onZero: false, lineStyle: { color: '#000000' } },
      splitLine: { show: false },
    },
    yAxis: {
      ...axeValeur({ nom: 'Robustesse', formatteur: (v) => `${v}` }),
      min: yMin,
      max: yMax,
      interval: pasEntier(yMax),
      // Le nom est à l'horizontale (jamais pivoté), centré sur la hauteur de
      // l'axe : entre "sans regret" (haut) et "dépend des scénarios" (bas).
      nameLocation: 'middle',
      nameGap: 34,
      nameTextStyle: { fontSize: 14, color: '#000000', fontWeight: 600, align: 'right' },
      // 0 en haut, les valeurs augmentent vers le bas.
      inverse: true,
      // Sans ça, l'axe se dessinerait là où X vaut 0, c'est-à-dire au milieu.
      axisLine: { show: true, onZero: false, lineStyle: { color: '#000000' } },
      splitLine: { show: false },
    },
    series: [
      {
        type: 'scatter',
        // Un point posé exactement sur une borne (max entier) ne doit pas être
        // rogné par la zone du graphique.
        clip: false,
        symbolSize: 18,
        itemStyle: { color: '#1F3864' },
        label: {
          show: true,
          position: 'right',
          fontSize: 14,
          color: '#000000',
          formatter: ({ data: point }) => String(point.numero),
        },
        // Numéro = rang dans la liste reçue (1, 2, 3...), repris dans le tableau.
        data: data.map((d, i) => ({
          nom: d.solution,
          numero: i + 1,
          ecartType: d.ecartType,
          value: [d.impact, d.ecartType],
        })),
        markArea: {
          silent: true,
          itemStyle: { opacity: 1 },
          data: [
            // Les quatre quadrants, bornés par 0 en X et la moitié de l'axe Y.
            // X et Y sont inversés : à l'écran, la gauche = valeurs de X
            // positives et le haut = Y faible, d'où les bornes ci-dessous.
            [{ xAxis: 0, yAxis: yMin, itemStyle: { color: COULEURS_QUADRANTS.hautGauche } },
             { xAxis: xMax, yAxis: yMax / 2 }],
            [{ xAxis: xMin, yAxis: yMin, itemStyle: { color: COULEURS_QUADRANTS.hautDroite } },
             { xAxis: 0, yAxis: yMax / 2 }],
            [{ xAxis: 0, yAxis: yMax / 2, itemStyle: { color: COULEURS_QUADRANTS.basGauche } },
             { xAxis: xMax, yAxis: yMax }],
            [{ xAxis: xMin, yAxis: yMax / 2, itemStyle: { color: COULEURS_QUADRANTS.basDroite } },
             { xAxis: 0, yAxis: yMax }],
          ],
        },
      },
    ],
    graphic: [
      // Axe Y (robustesse), à gauche : "Robustesse" (nom de l'axe) est au
      // milieu, entre les deux extrémités.
      {
        type: 'text', left: 4, top: 90,
        style: { text: 'sans regret', fontSize: 14, fill: '#000000', fontWeight: 'normal' },
      },
      {
        type: 'text', left: 4, bottom: 20,
        style: { text: 'Variable', fontSize: 14, fill: '#000000', fontWeight: 'normal' },
      },
      // Axe X (impact), en haut : le nom de l'axe est au milieu.
      {
        type: 'text', left: 130, top: 8,
        style: { text: 'Le risque augmente', fontSize: 14, fill: '#000000' },
      },
      {
        type: 'text', right: 40, top: 8,
        style: { text: 'Le risque diminue', fontSize: 14, fill: '#000000' },
      },
    ],
  };

  // Graphique à gauche, tableau de correspondance numéro -> nom complet à droite.
  return (
    <div className="oad-pheno-layout" style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div style={{ flex: '1.5 1 0', minWidth: 0, display: 'flex' }}>
        <ResponsiveECharts option={option} style={{ flex: '1 1 auto', minHeight: 460 }} />
      </div>

      {/* padding : le contour noir des lignes déborde d'1px, sans ça il serait
          rogné par le défilement horizontal. */}
      <div style={{ flex: '1 1 0', overflowX: 'auto', padding: '0 1px' }}>
        <table className="oad-data-table">
          <thead>
            <tr>
              <th>N°</th>
              <th style={{ textAlign: 'left' }}>Solution d'adaptation</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d, i) => (
              // L'inline style l'emporte sur le zèbrage CSS des lignes paires.
              <tr key={d.solution} style={{ background: couleurQuadrant(d, yMax) }}>
                <td style={stables.has(d.solution) ? contourLigne(true, false) : undefined}>
                  <b>{i + 1}</b>
                </td>
                <td
                  style={{
                    textAlign: 'left',
                    ...(stables.has(d.solution) ? contourLigne(false, true) : null),
                  }}
                >
                  {d.solution}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
